import React, { useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link, useNavigate } from 'react-router-dom'

interface Props {
  closeSharePointModal: () => void
}

const initialValues = {
  clientid: '',
  clientsecret: '',
  tenantid: '',
  openapikey: ''
}

const sharepointSchema = Yup.object().shape({
  clientid: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Client ID is required'),
  clientsecret: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Client Secret is required'),
  tenantid: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Tenant ID is required'),
  openapikey: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Open Api Key is required')
})

const SharePointModal: React.FC<Props> = ({ closeSharePointModal }) => {
  const [loadingOL, setLoading] = useState(false)
  const [isShow, setishow] = useState<boolean>(false)
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues,
    validationSchema: sharepointSchema,
    onSubmit: (values) => {
      // Perform any additional logic here if needed
      // For now, let's navigate to '/integrations/sharepoint'
      // navigate('/integrations/sharepoint')
      setishow(true)
    }
  })

  return (
    <div className='card w-600px h-600 mb-4 p-2 position-relative text-center '>
      <button
        type='button'
        className='btn-close position-absolute top-0 end-0 m-n4 rounded-circle'
        aria-label='Close'
        onClick={closeSharePointModal}
        style={{
          backgroundColor: 'rgba(218, 222, 243)',
          height: '25px',
          width: '25px',
          color: 'rgba(52, 60, 106)'
        }}
      ></button>

      {!isShow ? (
        <>
          <div className='d-flex justify-content-between align-items-center px-3 py-2'>
            <h2 className='mb-0'>SharePoint Configuration</h2>
          </div>

          <div className='d-flex justify-content-between align-items-center px-3 py-2'>
            <p className='mb-0'>Fill all the details given in the form.</p>
          </div>

          <div className='d-flex flex-column mb-10 fv-row fv-plugins-icon-container'>
            <div className='d-flex align-items-start mt-6 px-3 py-2'>
              <label className='fs-6 fw-bold mb-2'>Client ID:</label>
            </div>
            <input
              placeholder='Enter client id'
              type='email'
              autoComplete='off'
              {...formik.getFieldProps('clientid')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                { 'is-invalid': formik.touched.clientid && formik.errors.clientid },
                {
                  'is-valid': formik.touched.clientid && !formik.errors.clientid
                }
              )}
            />
            {formik.touched.clientid && formik.errors.clientid && (
              <div className='d-flex align-items-start mt-1 n2 px-3 py-2'>
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.clientid}</span>
                  </div>
                </div>
              </div>
            )}

            <div className='d-flex align-items-start mt-6 px-3 py-2'>
              <label className='fs-6 fw-bold mb-2'>Client Secret:</label>
            </div>
            <input
              placeholder='Enter client secret'
              type='email'
              autoComplete='off'
              {...formik.getFieldProps('clientsecret')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                { 'is-invalid': formik.touched.clientsecret && formik.errors.clientsecret },
                {
                  'is-valid': formik.touched.clientsecret && !formik.errors.clientsecret
                }
              )}
            />
            {formik.touched.clientsecret && formik.errors.clientsecret && (
              <div className='d-flex align-items-start mt-1 n2 px-3 py-2'>
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.clientsecret}</span>
                  </div>
                </div>
              </div>
            )}

            <div className='d-flex align-items-start mt-6 px-3 py-2'>
              <label className='fs-6 fw-bold mb-2'>Tenant ID:</label>
            </div>
            <input
              placeholder='Enter tenant id'
              type='email'
              autoComplete='off'
              {...formik.getFieldProps('tenantid')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                { 'is-invalid': formik.touched.tenantid && formik.errors.tenantid },
                {
                  'is-valid': formik.touched.tenantid && !formik.errors.tenantid
                }
              )}
            />
            {formik.touched.tenantid && formik.errors.tenantid && (
              <div className='d-flex align-items-start mt-1 n2 px-3 py-2'>
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.tenantid}</span>
                  </div>
                </div>
              </div>
            )}

            <div className='d-flex align-items-start mt-6 px-3 py-2'>
              <label className='fs-6 fw-bold mb-2'>Open Api Key:</label>
            </div>
            <input
              placeholder='Enter open api key'
              type='email'
              autoComplete='off'
              {...formik.getFieldProps('openapikey')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                { 'is-invalid': formik.touched.openapikey && formik.errors.openapikey },
                {
                  'is-valid': formik.touched.openapikey && !formik.errors.openapikey
                }
              )}
            />
            {formik.touched.openapikey && formik.errors.openapikey && (
              <div className='d-flex align-items-start mt-1 n2 px-3 py-2'>
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.openapikey}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className='text-start'>
            <button
              type='button'
              id='kt_sign_up_submit'
              className='btn btn-lg btn-primary w-100 mb-5'
              disabled={formik.isSubmitting || !formik.isValid}
              onClick={() => {
                formik.handleSubmit()
              }}
            >
              {!loadingOL && <span className='indicator-label'>Submit</span>}
              {loadingOL && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className='d-flex justify-content-between align-items-center px-3 py-2'>
            <h2 className='mb-0'>SharePoint Url</h2>
          </div>

          <div className='d-flex justify-content-between align-items-center px-3 py-2'>
            <p className='mb-0'>botgo.io</p>
          </div>
        </>
      )}
    </div>
  )
}

export default SharePointModal
